import {
  CacheConfig,
  FetchFunction,
  GraphQLResponse,
  Network,
  Observable,
  PayloadData,
  PayloadError,
  RequestParameters,
  SubscribeFunction,
  Variables,
} from "relay-runtime";

import { fetch } from "expo/fetch";
import {
  getMultipartBoundary,
  MultipartParser,
} from "@remix-run/multipart-parser";

type Request = {
  params: RequestParameters;
  variables: Variables;
  cacheConfig: CacheConfig;
  signal?: AbortSignal;

  url: string;
  headers: Record<string, string>;
};

export type MiddlewareFn = (
  request: Request
) => AsyncGenerator<GraphQLResponse, void, void>;

type Middleware = (next: MiddlewareFn) => MiddlewareFn;

interface GraphQLResponseChunk {
  data?: PayloadData;
  incremental?: ({
    id?: string;
    path?: (number | string)[];
    subPath?: (number | string)[];
    label?: string;
    errors?: PayloadError[];
  } & (
    | {
        data?: PayloadData;
        items?: never;
      }
    | {
        data?: never;
        items?: PayloadData[];
      }
  ))[];
  pending?: {
    id: string;
    path?: (number | string)[];
    label?: string;
  }[];
  errors?: PayloadError[] | undefined;
  hasNext?: boolean;
  extensions?: unknown;
}

type RequestConfig = {
  url: string;
  headers?: Record<string, string>;
};
const makeFetchRequest: MiddlewareFn = async function* (request: Request) {
  const body = JSON.stringify({
    operationName: request.params.name,
    query: request.params.text,
    variables: request.variables,
  });
  let headers = { "Content-Type": "application/json", ...request.headers };

  try {
    const res = await fetch(request.url, {
      method: "POST",
      headers: headers,
      signal: request.signal,
      body,
    });

    if (!res.ok) {
      throw await HTTPError.fromResponse(res);
    }

    const contentType = res.headers.get("content-type");
    if (contentType?.startsWith("multipart/")) {
      const boundary = getMultipartBoundary(contentType);
      if (!boundary) {
        throw new Error(`Missing multipart boundary '${contentType}'`);
      }

      const body = res.body;
      if (!body) {
        throw new Error("Missing response body");
      }

      const reader = body.getReader();
      const parser = new MultipartParser(boundary);

      let initial: GraphQLResponseChunk;
      const pendingById = new Map<
        string,
        { path?: (number | string)[]; label?: string }
      >();

      let leading = true;
      while (true) {
        let { value, done } = await reader.read();
        if (value) {
          let i = 0;
          while (leading && i < value.length) {
            const b = value[i];
            // drop leading '\n', '\r' or ' '
            if (b === 10 || b === 13 || b === 32) {
              i++;
            } else {
              leading = false;
            }
          }
          if (i < value.length) {
            for await (const chunk of parser.write(
              i > 0 ? value.subarray(i) : value
            )) {
              const partBody = JSON.parse(chunk.text) as GraphQLResponseChunk;
              initial ??= partBody;

              if (partBody.pending) {
                for (const p of partBody.pending) {
                  pendingById.set(p.id, { path: p.path, label: p.label });
                }
              }

              if (partBody.data) {
                const base: GraphQLResponse = {
                  data: partBody.data,
                  errors: partBody.errors,
                } as GraphQLResponse;
                if (partBody.hasNext !== undefined) {
                  (base as any).hasNext = partBody.hasNext;
                }
                if (partBody.extensions !== undefined) {
                  (base as any).extensions = partBody.extensions as any;
                }
                yield base;
              }

              if (partBody.incremental) {
                for (const incremental of partBody.incremental) {
                  const basePath =
                    incremental.path !== undefined
                      ? incremental.path
                      : incremental.subPath && incremental.id
                        ? pendingById.get(incremental.id)?.path
                        : undefined;
                  const path =
                    incremental.subPath && basePath
                      ? [...(basePath ?? []), ...incremental.subPath]
                      : basePath;

                  const errorsToEmit =
                    incremental.errors !== undefined
                      ? incremental.errors
                      : partBody.errors;

                  if (incremental.data) {
                    const incRes: GraphQLResponse = {
                      data: incremental.data,
                      path,
                      label: incremental.label,
                      errors: errorsToEmit,
                    } as GraphQLResponse;
                    if (partBody.hasNext !== undefined) {
                      (incRes as any).hasNext = partBody.hasNext;
                    }
                    if (partBody.extensions !== undefined) {
                      (incRes as any).extensions = partBody.extensions as any;
                    }
                    yield incRes;
                  }
                  if (incremental.items) {
                    for (const item of incremental.items) {
                      const itemRes: GraphQLResponse = {
                        data: item,
                        path,
                        label: incremental.label,
                        errors: errorsToEmit,
                      } as GraphQLResponse;
                      if (partBody.hasNext !== undefined) {
                        (itemRes as any).hasNext = partBody.hasNext;
                      }
                      if (partBody.extensions !== undefined) {
                        (itemRes as any).extensions =
                          partBody.extensions as any;
                      }
                      yield itemRes;
                    }
                  }
                }
              }
            }
          }
        }
        if (done) break;
      }
    } else {
      yield await res.json();
    }
  } catch (e) {
    throw e;
  }
};

function getAbortController() {
  if (typeof AbortController !== "undefined") {
    return new AbortController();
  }
  return null;
}

export const create = (
  config:
    | RequestConfig
    | Promise<RequestConfig>
    | ((
        request: Omit<Request, "url" | "headers">
      ) => RequestConfig | Promise<RequestConfig>),
  middlewares: Middleware[],
  subscribeFn?: SubscribeFunction
) => {
  let doFetch = makeFetchRequest;

  for (const middleware of middlewares) {
    doFetch = middleware(doFetch);
  }

  const fetchFn: FetchFunction = (
    params: RequestParameters,
    variables: Variables,
    cacheConfig: CacheConfig
  ) => {
    let cancelled = false;
    const abortController = getAbortController();
    return Observable.create((sink) => {
      (async () => {
        try {
          const partialRequest = {
            params,
            variables,
            cacheConfig,
            signal: abortController?.signal,
            headers: {
              "Content-Type": "application/json",
            },
          };

          let requestConfig;
          if (typeof config === "function") {
            requestConfig = await config(partialRequest);
          } else {
            requestConfig = await config;
          }

          const request = {
            ...partialRequest,
            url: requestConfig.url,

            headers: {
              ...partialRequest.headers,
              ...requestConfig.headers,
            },
          };

          for await (const response of doFetch(request)) {
            sink.next(response);

            if (cancelled) {
              break;
            }
          }
          sink.complete();
        } catch (e: any) {
          if (e instanceof Error) {
            sink.error(e);
          } else {
            sink.error(new Error(e.toString()));
          }
        }
      })();

      return () => {
        cancelled = true;
        abortController?.abort();
      };
    });
  };

  return Network.create(fetchFn, subscribeFn);
};

export namespace Middlewares {
  export const auth = (config: {
    token: () => string | Promise<string>;
    refreshToken?: () => Promise<string>;
  }): Middleware => {
    let token = config.token();

    return (next) => {
      return async function* (request: Request) {
        request.headers["Authorization"] = `Bearer ${await token}`;

        try {
          for await (const res of next(request)) {
            yield res;
          }
        } catch (e) {
          if (
            e instanceof HTTPError &&
            e.status === 401 &&
            config.refreshToken
          ) {
            token = config.refreshToken();

            // retry
            request.headers["Authorization"] = `Bearer ${await token}`;
            for await (const res of next(request)) {
              yield res;
            }
          } else {
            throw e;
          }
        }
      };
    };
  };

  const timeoutSymbol = Symbol("timeout");

  export const retry = (config: {
    timeout: number;
    retryDelays: number[];
    statusCodes: number[];
  }): Middleware => {
    return (next) => {
      return async function* (request: Request) {
        // Allow one initial attempt plus one attempt per configured delay
        const maxAttempts = config.retryDelays.length + 1;

        let attempts = 0;

        let lastError;
        while (attempts < maxAttempts) {
          // Apply delay before each retry attempt (but not before the initial attempt)
          if (attempts >= 1) {
            await new Promise((resolve) =>
              setTimeout(resolve, config.retryDelays[attempts - 1])
            );
          }

          let timeoutId: any;
          const makeTimeout = () =>
            new Promise<typeof timeoutSymbol>((resolve) => {
              timeoutId = setTimeout(
                () => resolve(timeoutSymbol),
                config.timeout
              );
            });
          let timeoutFuture = makeTimeout();

          try {
            const responseStream = next(request);

            let response:
              | typeof timeoutSymbol
              | IteratorResult<GraphQLResponse, void>;
            while (true) {
              response = await Promise.race([
                timeoutFuture,
                responseStream.next(),
              ]);

              if (response === timeoutSymbol) {
                throw timeoutSymbol;
              } else if (response.done) {
                break;
              } else {
                yield response.value;
                // Reset timeout after each yielded chunk
                clearTimeout(timeoutId);
                timeoutFuture = makeTimeout();
              }
            }
            clearTimeout(timeoutId);
            return;
          } catch (e) {
            const shouldRetry =
              e instanceof HTTPError
                ? config.statusCodes.indexOf(e.status) !== -1
                : e === timeoutSymbol ||
                  (e && (e as any).name !== "AbortError");
            if (shouldRetry) {
              lastError = e;
              attempts += 1;
            } else {
              throw e;
            }
          }
        }

        throw lastError;
      };
    };
  };
}

class HTTPError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly body: string
  ) {
    super(`HTTP Error ${status} ${statusText}`);
  }

  static async fromResponse(response: Response): Promise<HTTPError> {
    return new HTTPError(
      response.status,
      response.statusText,
      await response.text()
    );
  }

  toString() {
    return `HTTP Error ${this.status} ${this.statusText}\n${this.body}`;
  }
}

export function formatPayloadErrors(
  request: Request,
  errors: PayloadError[]
): string {
  const CONTEXT_BEFORE = 20;
  const CONTEXT_LENGTH = 60;

  let queryLines = request.params.text?.split("\n") ?? [];

  return errors
    .map(({ locations, message }, ii) => {
      const prefix = `${ii + 1}. `;
      const indent = " ".repeat(prefix.length);

      const locationMessage =
        locations && queryLines.length
          ? "\n" +
            locations
              .map(({ column, line }) => {
                const queryLine = queryLines[line - 1];
                const offset = Math.min(column - 1, CONTEXT_BEFORE);
                return [
                  queryLine.substr(column - 1 - offset, CONTEXT_LENGTH),
                  `${" ".repeat(Math.max(offset, 0))}^^^`,
                ]
                  .map((messageLine) => indent + messageLine)
                  .join("\n");
              })
              .join("\n")
          : "";
      return prefix + message + locationMessage;
    })
    .join("\n");
}
